import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { auth, db } from './firebase';
import './LessonCalendar.css';

function LessonCalendar({ onClose, onActivateSchool }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [schools, setSchools] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [schoolEvents, setSchoolEvents] = useState([]);
  const [termStructures, setTermStructures] = useState({});
  const [termStructureDetails, setTermStructureDetails] = useState({}); 
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const handleActivateSchool = (school) => {
  setSelectedLesson(null); // Close modal
  onActivateSchool(school); // Navigate to AddSchool with prospect data
};
  useEffect(() => {
    loadCalendarData();
  }, []);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // ✅ UPDATED: Load ALL schools (including prospects)
      const schoolsQuery = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      
      const schoolsSnapshot = await getDocs(schoolsQuery);
      console.log('ðŸ« Schools loaded (including prospects):', schoolsSnapshot.size);

      const termStructuresMap = {};
      const termStructureDetailsMap = {};

      const schoolsList = await Promise.all(
        schoolsSnapshot.docs.map(async (docSnap) => {
          const schoolData = { id: docSnap.id, ...docSnap.data() };
          
          // ✅ Only load term structures for active schools (prospects don't have terms)
          if (schoolData.schoolStatus !== 'prospect' && schoolData.termStructureRef) {
            try {
              const termStructureDoc = await getDoc(schoolData.termStructureRef);
              if (termStructureDoc.exists()) {
                const termData = termStructureDoc.data();
                
                if (termData.franchiseeRef && termData.franchiseeRef.id === user.uid) {
                  termStructuresMap[schoolData.id] = termData;
                  termStructureDetailsMap[termStructureDoc.id] = termData;
                  console.log('✅ Term structure loaded for', schoolData.schoolName);
                } else {
                  console.warn('âš ï¸ Term structure belongs to different franchisee for', schoolData.schoolName);
                }
              }
            } catch (termError) {
              console.warn('âš ï¸ Could not load term structure for', schoolData.schoolName);
            }
          }
          
          return schoolData;
        })
      );
      
      setSchools(schoolsList);
      setTermStructures(termStructuresMap);
      setTermStructureDetails(termStructureDetailsMap);

      // Load Holidays
      try {
        const holidaysQuery = query(
          collection(db, 'holidays'),
          where('franchiseeRef', '==', franchiseeRef)
        );
        const holidaysSnapshot = await getDocs(holidaysQuery);
        console.log('✅ Holidays loaded:', holidaysSnapshot.size);
        
        const holidaysList = holidaysSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setHolidays(holidaysList);
      } catch (holidayError) {
        console.error('❌ Error loading holidays:', holidayError);
        setHolidays([]);
      }
      // NEW: Load School Events (demos and parents meetings)
      try {
        const eventsQuery = query(
          collection(db, 'schoolEvents'),
          where('franchiseeRef', '==', franchiseeRef)
        );
        const eventsSnapshot = await getDocs(eventsQuery);
        console.log('📅 School events loaded:', eventsSnapshot.size);
        
        const eventsList = eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSchoolEvents(eventsList);
      } catch (eventsError) {
        console.error('❌ Error loading school events:', eventsError);
        setSchoolEvents([]);
      }

      console.log('✅ Calendar data loaded successfully');
      console.log(`ðŸ“Š Loaded ${schoolsList.length} schools (${schoolsList.filter(s => s.schoolStatus === 'prospect').length} prospects)`);
      setLoading(false);
    } catch (error) {
      console.error('âŒ Error loading calendar data:', error);
      alert('Error loading calendar data. Please check console for details.');
      setLoading(false);
    }
  };

  const generateMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const generateWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    
    return days;
  };

  const hasHolidayForSchool = (date, school) => {
    if (!date) return false;
    
    const holidaysOnDate = getHolidaysForDate(date);
    
    for (const holiday of holidaysOnDate) {
      if (holiday.holidayType === 'public') {
        return true;
      }
      
      if (holiday.holidayType === 'school' && holiday.linkedTermStructureRef && school.termStructureRef) {
        if (holiday.linkedTermStructureRef.id === school.termStructureRef.id) {
          return true;
        }
        continue;
      }
      
      if (holiday.appliesToAllSchools && holiday.holidayType !== 'school') {
        return true;
      }
      
      if (holiday.schoolRef && school.id === holiday.schoolRef.id) {
        return true;
      }
    }
    
    return false;
  };

  const getHolidayColorClass = (holiday) => {
    if (holiday.holidayType === 'public') {
      return 'public';
    }
    
    if (holiday.linkedTermStructureRef) {
      const termStructureId = holiday.linkedTermStructureRef.id;
      const termStructure = termStructureDetails[termStructureId];
      
      if (termStructure) {
        if (termStructure.lessonsPerTerm === 8) {
          return 'term-8-lessons';
        } else if (termStructure.lessonsPerTerm === 10) {
          return 'term-10-lessons';
        }
      }
    }
    
    return 'term-break';
  };

  // ✅ NEW: Get prospect demos for a specific date
  const getProspectDemosForDate = (date) => {
    if (!date) return [];
    
    const dateStr = formatDateToCompare(date);
    
    const filteredSchools = selectedSchool === 'all' 
      ? schools 
      : schools.filter(s => s.id === selectedSchool);
    
    return filteredSchools
      .filter(school => school.schoolStatus === 'prospect')
      .filter(school => {
        if (!school.prospectInfo?.demonstrationDate) return false;
        
        const demoDate = school.prospectInfo.demonstrationDate.toDate 
          ? school.prospectInfo.demonstrationDate.toDate() 
          : new Date(school.prospectInfo.demonstrationDate);
        
        return formatDateToCompare(demoDate) === dateStr;
      })
      .map(school => ({
        school: school,
        time: school.prospectInfo.demonstrationTime,
        type: 'prospect-demo'
      }));
  };

  // NEW: Get scheduled demos for active schools
  const getScheduledDemosForDate = (date) => {
    if (!date) return [];
    
    const dateStr = formatDateToCompare(date);
    
    const filteredSchools = selectedSchool === 'all' 
      ? schools 
      : schools.filter(s => s.id === selectedSchool);
    
    const demos = [];
    
    // Loop through all schools and check their demoLessons array
    for (const school of filteredSchools) {
      if (!school.demoLessons || school.demoLessons.length === 0) continue;
      
      // Check each demo lesson
      for (const demo of school.demoLessons) {
        // Skip if not scheduled status
        if (demo.status !== 'scheduled') continue;
        
        // Format demo date for comparison
        const demoDateStr = demo.demoDate; // Already in YYYY-MM-DD format
        
        if (demoDateStr === dateStr) {
          demos.push({
            school: school,
            demo: demo,
            time: demo.demoTime,
            type: 'scheduled-demo'
          });
        }
      }
    }
    
    return demos;
  };

  // NEW: Get school events (from schoolEvents collection) for a specific date
  const getSchoolEventsForDate = (date) => {
    if (!date) return [];
    
    const dateStr = formatDateToCompare(date);
    
    const filteredSchools = selectedSchool === 'all' 
      ? schools 
      : schools.filter(s => s.id === selectedSchool);
    
    return schoolEvents
      .filter(event => {
        // Check if event date matches
        const eventDate = event.date?.toDate ? event.date.toDate() : new Date(event.date);
        const eventDateStr = formatDateToCompare(eventDate);
        
        if (eventDateStr !== dateStr) return false;
        
        // Filter by selected school if not "all"
        if (selectedSchool !== 'all') {
          const eventSchoolId = event.schoolRef?.id;
          return filteredSchools.some(s => s.id === eventSchoolId);
        }
        
        return true;
      })
      .map(event => {
        // Find the school for this event
        const eventSchoolId = event.schoolRef?.id;
        const school = schools.find(s => s.id === eventSchoolId);
        
        return {
          school: school,
          event: event,
          time: event.time,
          type: event.type // 'demo' or 'parents_meeting'
        };
      });
  };

  const getLessonsForDate = (date) => {
    if (!date) return [];
    
    const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
    
    const filteredSchools = selectedSchool === 'all' 
      ? schools 
      : schools.filter(s => s.id === selectedSchool);
    
    // ✅ UPDATED: Only show regular lessons for ACTIVE schools
    return filteredSchools
      .filter(school => school.schoolStatus === 'active' || !school.schoolStatus) // Active or legacy (no status)
      .filter(school => school.isActive !== false) // Also check old isActive field
      .filter(school => school.lessonDay === dayName)
      .filter(school => {
        if (!termStructures[school.id]) return true;
        return isDateInTerm(date, school);
      })
      .filter(school => {
        return !hasHolidayForSchool(date, school);
      })
      .map(school => ({
        school: school,
        time: school.lessonTime,
        type: 'lesson'
      }));
  };

  const isDateInTerm = (date, school) => {
    const termStructure = termStructures[school.id];
    if (!termStructure || !termStructure.termDates) return false;
    
    const dateStr = formatDateToCompare(date);
    
    for (const termKey in termStructure.termDates) {
      const term = termStructure.termDates[termKey];
      if (!term.start || !term.end) continue;
      
      const termStart = parseTermDate(term.start, date.getFullYear());
      const termEnd = parseTermDate(term.end, date.getFullYear());
      
      if (dateStr >= formatDateToCompare(termStart) && dateStr <= formatDateToCompare(termEnd)) {
        return true;
      }
    }
    
    return false;
  };

  const getHolidaysForDate = (date) => {
    if (!date) return [];
    
    return holidays.filter(holiday => {
      let holidayStart, holidayEnd;
      
      if (holiday.startDate?.toDate) {
        holidayStart = holiday.startDate.toDate();
      } else {
        holidayStart = new Date(holiday.startDate);
      }
      
      if (holiday.endDate?.toDate) {
        holidayEnd = holiday.endDate.toDate();
      } else {
        holidayEnd = new Date(holiday.endDate);
      }
      
      const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const startDate = new Date(holidayStart.getFullYear(), holidayStart.getMonth(), holidayStart.getDate());
      const endDate = new Date(holidayEnd.getFullYear(), holidayEnd.getMonth(), holidayEnd.getDate());
      
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  const getTermBoundary = (date) => {
    if (!date) return null;
    
    const dateStr = formatDateToCompare(date);
    
    for (const school of schools) {
      const termStructure = termStructures[school.id];
      if (!termStructure || !termStructure.termDates) continue;
      
      for (const termKey in termStructure.termDates) {
        const term = termStructure.termDates[termKey];
        if (!term.start || !term.end) continue;
        
        const termStart = parseTermDate(term.start, date.getFullYear());
        const termEnd = parseTermDate(term.end, date.getFullYear());
        
        if (dateStr === formatDateToCompare(termStart)) {
          return { type: 'start', term: termKey };
        }
        if (dateStr === formatDateToCompare(termEnd)) {
          return { type: 'end', term: termKey };
        }
      }
    }
    
    return null;
  };

  const formatDateToCompare = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const parseTermDate = (termDateStr, year) => {
    const [month, day] = termDateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return formatDateToCompare(date) === formatDateToCompare(today);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleLessonClick = (lesson) => {
    setSelectedLesson(lesson);
  };

  if (loading) {
    return (
      <div className="calendar-container">
        <div className="loading">Loading calendar...</div>
      </div>
    );
  }

  const days = viewMode === 'month' ? generateMonthDays() : generateWeekDays();

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <div className="header-left">
          <h1>📅 Lesson Calendar</h1>
          <p>View lessons, demos, holidays, and term schedules</p>
        </div>
      </div>

      <div className="calendar-controls">
        <div className="controls-left">
          <button
            onClick={() => viewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)}
            className="btn-nav"
          >
            ← Previous
          </button>
          <button onClick={goToToday} className="btn-today">
            Today
          </button>
          <button
            onClick={() => viewMode === 'month' ? navigateMonth(1) : navigateWeek(1)}
            className="btn-nav"
          >
            Next →
          </button>
          
          <h2 className="current-month">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
        </div>

        <div className="controls-right">
          <select
            value={selectedSchool}
            onChange={(e) => setSelectedSchool(e.target.value)}
            className="school-filter"
          >
            <option value="all">All Schools</option>
            {schools.filter(s => s.schoolStatus === 'active' || !s.schoolStatus).map(school => (
              <option key={school.id} value={school.id}>
                {school.schoolName}
              </option>
            ))}
            <optgroup label="Prospects">
              {schools.filter(s => s.schoolStatus === 'prospect').map(school => (
                <option key={school.id} value={school.id}>
                  🟡 {school.schoolName}
                </option>
              ))}
            </optgroup>
          </select>

          <div className="view-toggle">
            <button
              onClick={() => setViewMode('month')}
              className={viewMode === 'month' ? 'active' : ''}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={viewMode === 'week' ? 'active' : ''}
            >
              Week
            </button>
          </div>
        </div>
      </div>

      {/* ✅ UPDATED LEGEND */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color lesson"></span>
          <span>Lesson</span>
        </div>
        <div className="legend-item">
          <span className="legend-color scheduled-demo"></span>
          <span>Scheduled Demo</span>
        </div>
        <div className="legend-item">
          <span className="legend-color parents-meeting"></span>
          <span>Parents Meeting</span>
        </div>
        <div className="legend-item">
          <span className="legend-color prospect-demo"></span>
          <span>Prospect Demo</span>
        </div>
        <div className="legend-item">
          <span className="legend-color holiday-public"></span>
          <span>Public Holiday</span>
        </div>
        <div className="legend-item">
          <span className="legend-color holiday-8-lessons"></span>
          <span>Holiday (8 Lesson Term)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color holiday-10-lessons"></span>
          <span>Holiday (10 Lesson Term)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color term-boundary"></span>
          <span>Term Start/End</span>
        </div>
      </div>

      <div className={`calendar-grid ${viewMode}`}>
        <div className="day-header">Sun</div>
        <div className="day-header">Mon</div>
        <div className="day-header">Tue</div>
        <div className="day-header">Wed</div>
        <div className="day-header">Thu</div>
        <div className="day-header">Fri</div>
        <div className="day-header">Sat</div>

        {days.map((date, index) => {
  const lessons = getLessonsForDate(date);
  const prospectDemos = getProspectDemosForDate(date);
  const scheduledDemos = getScheduledDemosForDate(date);
  const schoolEventsForDate = getSchoolEventsForDate(date);
  const holidaysOnDate = getHolidaysForDate(date);
  const termBoundary = getTermBoundary(date);
  const hasHoliday = holidaysOnDate.length > 0;
  
  // NEW: Combine all events and sort by time
  const allEvents = [
    ...prospectDemos,
    ...scheduledDemos,
    ...schoolEventsForDate,
    ...lessons
  ].sort((a, b) => {
    // Convert time to comparable format (HH:MM)
    const timeA = a.time || '00:00';
    const timeB = b.time || '00:00';
    return timeA.localeCompare(timeB);
  });
          
          return (
            <div
              key={index}
              className={`calendar-day ${!date ? 'empty' : ''} ${isToday(date) ? 'today' : ''} ${hasHoliday ? 'has-holiday' : ''}`}
            >
              {date && (
                <>
                  <div className="day-number">{date.getDate()}</div>
                  
                  {termBoundary && (
                    <div className="term-boundary">
                      {termBoundary.type === 'start' ? '🟢 Term Start' : '📴 Term End'}
                    </div>
                  )}
                  
                  {holidaysOnDate.map(holiday => (
                    <div 
                      key={holiday.id} 
                      className={`calendar-event holiday ${getHolidayColorClass(holiday)}`}
                    >
                      📴 {holiday.holidayName}
                    </div>
                  ))}
                  
                  {/* Render all events sorted by time */}
{allEvents.map((event, idx) => {
  // Determine event type and styling
  let className = 'calendar-event';
  let icon = '📅';
  let label = '';
  
  if (event.type === 'prospect-demo') {
    className += ' prospect-demo';
    icon = '🟡';
    label = `${event.time} - ${event.school.schoolName} (DEMO)`;
  } else if (event.type === 'scheduled-demo') {
    className += ' scheduled-demo';
    icon = '🟠';
    label = `${event.time} - ${event.school.schoolName} (DEMO)`;
  } else if (event.type === 'demo') {
    const isProspectDemo = event.school?.schoolStatus === 'prospect';
    className += isProspectDemo ? ' prospect-demo' : ' scheduled-demo';
    icon = isProspectDemo ? '🟡' : '🎨';
    label = `${event.time} - ${event.school?.schoolName || 'Unknown'} (DEMO)`;
  } else if (event.type === 'parents_meeting') {
    className += ' parents-meeting';
    icon = '👥';
    label = `${event.time} - ${event.school?.schoolName || 'Unknown'} (MEETING)`;
  } else {
    // Regular lesson
    className += ' lesson';
    icon = '📅';
    label = `${event.time} - ${event.school.schoolName}`;
  }
  
  return (
    <div
      key={`event-${idx}`}
      className={className}
      onClick={() => handleLessonClick(event)}
    >
      {icon} {label}
    </div>
  );
})}

                  
                </>
              )}
            </div>
          );
        })}
      </div>
      
      {/* ✅ UPDATED MODAL */}
      {selectedLesson && (
        <div className="modal-overlay" onClick={() => setSelectedLesson(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectedLesson.type === 'prospect-demo' ? '🟡 ' : '🔵 '}
                {selectedLesson.school.schoolName}
                {selectedLesson.type === 'prospect-demo' && ' (Prospect Demo)'}
              </h2>
              <button onClick={() => setSelectedLesson(null)} className="modal-close">✔</button>
            </div>
            <div className="modal-body">
              {(selectedLesson.type === 'demo' || selectedLesson.type === 'parents_meeting') && selectedLesson.event ? (
                // School Event (from schoolEvents collection)
                <>
                  <p><strong>{selectedLesson.type === 'demo' ? 'Demo' : 'Parents Meeting'} Time:</strong> {selectedLesson.time}</p>
                  <p><strong>School:</strong> {selectedLesson.school?.schoolName || 'N/A'}</p>
                  {selectedLesson.event.expectedStudents && (
                    <p><strong>Expected Students:</strong> {selectedLesson.event.expectedStudents}</p>
                  )}
                  {selectedLesson.event.notes && (
                    <p><strong>Notes:</strong> {selectedLesson.event.notes}</p>
                  )}
                  <p><strong>Status:</strong> <span style={{ color: '#10b981', fontWeight: 'bold' }}>{selectedLesson.event.status?.toUpperCase() || 'SCHEDULED'}</span></p>
                </>
              ) : selectedLesson.type === 'prospect-demo' ? (
                // Prospect Demo Info
                <>
                  <p><strong>Demo Time:</strong> {selectedLesson.time}</p>
                  <p><strong>Expected Students:</strong> {selectedLesson.school.prospectInfo?.expectedStudents || 0}</p>
                  <p><strong>Contact Person:</strong> {selectedLesson.school.prospectInfo?.contactPerson || 'N/A'}</p>
                  <p><strong>Email:</strong> {selectedLesson.school.prospectInfo?.contactEmail || 'N/A'}</p>
                  <p><strong>Phone:</strong> {selectedLesson.school.prospectInfo?.contactPhone || 'N/A'}</p>
                  <p><strong>Address:</strong> {selectedLesson.school.prospectInfo?.address || 'N/A'}</p>
                  {selectedLesson.school.prospectInfo?.notes && (
  <p><strong>Notes:</strong> {selectedLesson.school.prospectInfo.notes}</p>
)}

{/* ✅ ADD ACTIVATE BUTTON */}
<div className="modal-actions" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #f0f0f0' }}>
  <button 
    className="btn-activate-school"
    onClick={() => handleActivateSchool(selectedLesson.school)}
  >
    ✅ Activate School
  </button>
</div>
</>
              ) : selectedLesson.type === 'scheduled-demo' ? (
                // Scheduled Demo Info (for active schools)
                <>
                  <p><strong>Demo Time:</strong> {selectedLesson.time}</p>
                  <p><strong>Demo Date:</strong> {selectedLesson.demo.demoDate}</p>
                  <p><strong>Expected Children:</strong> {selectedLesson.demo.numberOfChildren}</p>
                  {selectedLesson.demo.notes && (
                    <p><strong>Notes:</strong> {selectedLesson.demo.notes}</p>
                  )}
                  <p><strong>School Contact:</strong> {selectedLesson.school.contactName || 'N/A'}</p>
                  <p><strong>Phone:</strong> {selectedLesson.school.contactPhone || 'N/A'}</p>
                  <p><strong>Email:</strong> {selectedLesson.school.contactEmail || 'N/A'}</p>
                  <p><strong>Status:</strong> <span style={{ color: '#10b981', fontWeight: 'bold' }}>{selectedLesson.demo.status.toUpperCase()}</span></p>
                </>
              ) : (
                // Regular Lesson Info
                <>
                  <p><strong>Lesson Time:</strong> {selectedLesson.time}</p>
                  <p><strong>Lesson Day:</strong> {selectedLesson.school.lessonDay}</p>
                  <p><strong>Lessons Per Term:</strong> {selectedLesson.school.lessonsPerTerm || 10}</p>
                  <p><strong>Contact:</strong> {selectedLesson.school.contactName || 'N/A'}</p>
                  <p><strong>Phone:</strong> {selectedLesson.school.contactPhone || 'N/A'}</p>
                  <p><strong>Students:</strong> {selectedLesson.school.numberOfStudents || 0}</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default LessonCalendar;